import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import FavoriteButton from "@/components/feature/FavoriteButton";
import SharePanel from "@/components/feature/SharePanel";
import ShortFilmCard from "@/components/feature/ShortFilmCard";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getFilmById, relatedFilms } from "@/lib/content";
import { formatDate, formatDuration, formatViews } from "@/utils/format";

export default function ShortFilmDetailPage() {
  const { id } = useParams<{ id: string }>();
  const film = getFilmById(id);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  const title = film ? `${film.title} - 小电影内容详情` : "短片未找到 - 小电影";
  const description = film
    ? film.synopsis.slice(0, 120)
    : "该短片可能已下线，返回短片分类浏览更多作品。";
  useDocumentMeta(title, description, film ? film.tags.join(",") : "小电影,影视短片");

  if (!film) {
    return (
      <PageShell>
        <div className="container-page py-20 text-center">
          <span className="inline-flex w-16 h-16 rounded-full bg-background-200 items-center justify-center">
            <i className="ri-film-line text-2xl text-foreground-500" />
          </span>
          <h1 className="mt-5 text-xl font-extrabold text-foreground-950">没有找到这部短片</h1>
          <p className="mt-2 text-sm text-foreground-600">链接可能已失效，去短片分类看看其他作品吧。</p>
          <Link
            to="/category/shortfilms"
            className="mt-6 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回短片分类
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </PageShell>
    );
  }

  const related = relatedFilms(film, 4);

  return (
    <PageShell>
      <div className="bg-background-100/70 border-b border-background-200">
        <div className="container-page py-4">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: "短片分类", to: "/category/shortfilms" },
              { label: "短片详情" },
            ]}
          />
        </div>
      </div>

      <article className="container-page py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-7 lg:gap-9">
          <div className="lg:col-span-2 min-w-0">
            <div className="w-full aspect-video rounded-lg overflow-hidden bg-background-200">
              <img
                src={film.cover}
                alt={`${film.title}短片封面`}
                title={`${film.title} - 小电影影视短片`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-5">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/category/shortfilms?type=${encodeURIComponent(film.type)}`}
                  className="inline-flex items-center h-6 px-2 rounded bg-primary-50 text-primary-700 text-[11px] font-medium cursor-pointer hover:bg-primary-100 transition-colors"
                >
                  {film.type}
                </Link>
                <span className="text-[11px] text-foreground-500 font-label">
                  {formatDuration(film.duration)}
                </span>
                <span className="w-1 h-1 rounded-full bg-background-300" />
                <span className="text-[11px] text-foreground-500">{formatDate(film.publishDate)}</span>
                <span className="w-1 h-1 rounded-full bg-background-300" />
                <span className="text-[11px] text-foreground-500">{formatViews(film.views)} 次观看</span>
              </div>

              <h1 className="mt-3 text-2xl md:text-[32px] font-extrabold leading-tight tracking-tight text-foreground-950">
                {film.title}
              </h1>
              <p className="mt-2 text-sm md:text-[15px] text-foreground-600 leading-relaxed">{film.tagline}</p>

              <div className="mt-5 flex flex-wrap items-center gap-2.5">
                <FavoriteButton filmId={film.id} />
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line text-base" />
                  分享
                </button>
                <span className="text-[11px] text-foreground-400">
                  收藏保存在本机浏览器，换设备不会同步
                </span>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-background-200">
              <h2 className="text-lg font-extrabold text-foreground-950 flex items-center gap-2">
                <span className="w-1.5 h-4 rounded-sm bg-accent-500" />
                剧情简介
              </h2>
              <p className="mt-3 text-[14px] md:text-[15px] leading-[1.9] text-foreground-700">
                {film.synopsis}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-2">
                {film.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center h-7 px-2.5 rounded-full bg-secondary-100 text-secondary-900 text-[12px]"
                  >
                    # {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-7 rounded-lg border border-background-200 bg-background-100 px-4 py-4 flex items-start gap-3">
              <i className="ri-information-line text-primary-600 text-lg mt-0.5" />
              <p className="text-[12px] leading-relaxed text-foreground-600">
                本站只做短片资料整理与推荐展示，不提供在线播放或下载。片中信息与图片均为演示内容。
              </p>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 lg:sticky lg:top-[126px]">
              <h2 className="text-sm font-bold text-foreground-950 mb-4">作品资料</h2>
              <dl className="space-y-3 text-[13px]">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">导演 / 作者</dt>
                  <dd className="text-foreground-900 font-medium text-right">{film.director}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">类型</dt>
                  <dd className="text-foreground-900 font-medium">{film.type}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">时长</dt>
                  <dd className="text-foreground-900 font-medium font-label">{film.duration} 分钟</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">发布日期</dt>
                  <dd className="text-foreground-900 font-medium font-label">{film.publishDate}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">观众评分</dt>
                  <dd className="flex items-center gap-1 text-accent-600 font-bold font-label">
                    <i className="ri-star-fill text-[13px]" />
                    {film.rating.toFixed(1)}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">热度</dt>
                  <dd className="text-foreground-900 font-medium font-label">{formatViews(film.views)}</dd>
                </div>
              </dl>

              <div className="mt-5 pt-4 border-t border-background-200">
                <h3 className="text-[13px] font-semibold text-foreground-800 mb-2">同类短片</h3>
                <div className="flex flex-col gap-1">
                  {related.slice(0, 3).map((item) => (
                    <Link
                      key={item.id}
                      to={`/detail/shortfilm/${item.id}`}
                      className="flex items-center gap-2.5 py-1.5 group cursor-pointer"
                    >
                      <div className="w-12 h-8 rounded overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={item.cover}
                          alt={item.title}
                          title={`${item.title} - 小电影`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <span className="text-[12px] text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {item.title}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-10 pt-8 border-t border-background-200">
          <div className="flex items-end justify-between gap-3 mb-5">
            <h2 className="text-xl font-extrabold text-foreground-950">你可能还想看</h2>
            <Link
              to={`/category/shortfilms?type=${encodeURIComponent(film.type)}`}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
            >
              更多{film.type}短片
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {related.map((item) => (
              <ShortFilmCard key={item.id} film={item} />
            ))}
          </div>
        </section>
      </article>

      <SharePanel open={shareOpen} onClose={() => setShareOpen(false)} title={`${film.title} - 小电影`} />
    </PageShell>
  );
}